// Пользователи (contracts/api.md, «Пользователи»).
import { z } from '../kernel/zod.ts';
import {
  EmailInputSchema,
  FullNameInputSchema,
  UserListItemSchema,
  UserSchema,
  UserStateSchema,
} from '../access/user.ts';
import { IdSchema } from '../kernel/common.ts';
import { PageQuerySchema, pageSchema, sortSchema } from '../kernel/page.ts';
import { UserKindSchema } from '../rights/roles.ts';

export const userSortFields = ['fullName', 'email', 'createdAt'] as const;

/**
 * GET /api/users: поиск по ФИО и почте, фильтры, сортировка, курсор. Без state — все, кроме архива.
 * fullName — пользователи с тем же ФИО, действующие и в архиве: подсказка при создании (T126);
 * сравнение без учёта регистра, «ё» как «е», пробелы и точки — разделители.
 */
export const UsersQuerySchema = z.object({
  ...PageQuerySchema.shape,
  q: z.string().trim().max(200).optional(),
  fullName: z.string().trim().min(1).max(200).optional(),
  /** 040 (FR-018): «Администратор», «Сотрудник», «Внешний», «Сопровождение». */
  kind: UserKindSchema.optional(),
  state: UserStateSchema.optional(),
  sort: sortSchema(userSortFields).default('fullName'),
});
export type UsersQuery = z.input<typeof UsersQuerySchema>;

export const UsersPageSchema = pageSchema(UserListItemSchema);

/**
 * POST /api/users (040, FR-005): сотрудник без выбора ролей и областей; admin — сразу роль
 * «Администратор» (назначение client_admin на всю установку).
 */
export const CreateUserBodySchema = z.object({
  email: EmailInputSchema,
  fullName: FullNameInputSchema,
  admin: z.boolean().default(false),
});
export type CreateUserBody = z.input<typeof CreateUserBodySchema>;

/**
 * 201 (035, FR-008): ссылки нет — её выпускает приветственное письмо при отправке; letterId — номер
 * письма (номер события user.welcome_requested), состояние — GET /api/users/{id}/welcome.
 */
export const CreateUserResponseSchema = z.object({
  user: UserSchema,
  welcome: z.object({ letterId: IdSchema }),
});
export type CreateUserResponse = z.infer<typeof CreateUserResponseSchema>;

/** PATCH /api/users/{id} с If-Match; смена почты отзывает все сеансы. */
export const UpdateUserBodySchema = z.object({
  fullName: FullNameInputSchema.optional(),
  email: EmailInputSchema.optional(),
});
export type UpdateUserBody = z.input<typeof UpdateUserBodySchema>;

/**
 * POST /api/users/{id}/archive (041, FR-006; research Р8): ownerTo — кто получит протоколы
 * уходящего; без поля — тот, кто переносит. Протоколов нет — поле не нужно.
 */
export const ArchiveUserBodySchema = z
  .strictObject({
    ownerTo: z.uuid({ error: 'Выберите действующего сотрудника компании' }).optional(),
  })
  .nullish();
export type ArchiveUserBody = z.input<typeof ArchiveUserBodySchema>;
